/**
 * Renders a QR code from plain Views, so it works on iOS, Android and web
 * without a canvas or an SVG dependency. See qrMatrix.ts for why.
 */
import React, { useMemo } from 'react';
import { View } from 'react-native';
import { qrRuns } from './qrMatrix';

interface QRCodeViewProps {
  value: string;
  /** Maximum side length in dp; the code snaps down to a whole dp per module. */
  size: number;
  /** Quiet zone, in modules. */
  margin?: number;
  testID?: string;
}

// Fixed colours, not theme tokens: scanners need dark-on-light regardless of
// the app's colour scheme.
const LIGHT = '#ffffff';
const DARK = '#000000';

export default function QRCodeView({ value, size, margin = 2, testID }: QRCodeViewProps) {
  const { size: n, runs } = useMemo(() => qrRuns(value), [value]);
  const cell = Math.max(1, Math.floor(size / (n + 2 * margin)));
  const side = cell * (n + 2 * margin);
  const inset = cell * margin;

  return (
    <View
      style={{ width: side, height: side, backgroundColor: LIGHT }}
      accessible={false}
      testID={testID}
    >
      {runs.map(([row, col, len]) => (
        <View
          key={`${row}:${col}`}
          style={{
            position: 'absolute',
            top: inset + row * cell,
            left: inset + col * cell,
            width: len * cell,
            height: cell,
            backgroundColor: DARK,
          }}
        />
      ))}
    </View>
  );
}
